import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

// Layouts
import MainLayout from "layouts/Main";

// Components
import { Button, InputField } from "@octopus-investments/deep-sea-components";
import PasswordField from "components/Inputs/PasswordField/PasswordField";
import PasswordChecks from "./PasswordChecks/PasswordChecks";

// Actions
import { setContent, setTwoFactorEnabled } from "store/slices/applicationSlice";
import { setCreatePasswordButtonLoader } from "store/slices/loadersSlice";
import { checkEmail, setPassword } from "store/slices/accountSlice";

// Tools
import { getStatusForAmpli, identifyUserInTrackingSystem, validatePassword } from "utils/helpers";
import { VALIDATION_ERRORS } from "utils/constants";

// Tracking
import { ampli } from "ampli";

// Styles
import "./CreatePasswordPage.scss";

export default function CreatePasswordPage() {
  const { email, isResetPassword, redirectUrl, isTwoFactorEnabled } = useSelector(
    ({ application }) => application,
  );
  const { isCreatePasswordButtonLoader } = useSelector(({ loaders }) => loaders);
  const [isLoading, setIsLoading] = useState(true);
  // Redux
  const reduxDispatch = useDispatch();

  // States
  const [showError, setShowError] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const [state, setState] = useState({
    password: "",
    confirmedPassword: "",
  });

  const { password, confirmedPassword } = state;

  const isBtnDisabled = Boolean(!validatePassword(password)) || !confirmedPassword;

  function handlePasswordKeyPress(e) {
    if (e.which === 13 && !isBtnDisabled) handleSubmitButton();
  }

  function handleSubmitButton() {
    if (isCreatePasswordButtonLoader) return;

    const AMPLI_LOCATION = Object.freeze({
      0: "set_password_page",
      1: "reset_password_page",
    });

    reduxDispatch(setCreatePasswordButtonLoader(true));

    if (password !== confirmedPassword) {
      setShowError(true);
      setErrorMsg(VALIDATION_ERRORS["passwordMatch"]);
      setSubmitted(true);

      ampli.passwordSetIntended({
        di_email_entered: email,
        di_passwords_match: false,
        di_status: "input error",
        location: AMPLI_LOCATION[+isResetPassword],
      });

      return reduxDispatch(setCreatePasswordButtonLoader(false));
    }

    setShowError(false);
    setSubmitted(true);

    reduxDispatch(setPassword(password)).then(result => {
      const { success, errors, status, isTemporaryLocked } = result.payload;
      ampli.passwordSetIntended({
        di_email_entered: email,
        di_passwords_match: true,
        di_status: getStatusForAmpli({ isTemporaryLocked, status }),
        location: AMPLI_LOCATION[+isResetPassword],
      });

      if (success) {
        identifyUserInTrackingSystem({ source: "password-page" });
        if (isTwoFactorEnabled) {
          globalThis.location.href = "/logout";
          return;
        }

        if (redirectUrl) {
          globalThis.location.href = redirectUrl;
          return;
        }
        const urlParams = new URLSearchParams(globalThis.location.search);
        const isRedirect = urlParams.has("returnUrl");
        const returnUrl = isRedirect
          ? urlParams.get("returnUrl")
          : process.env.REACT_APP_WELCOME_BTN_URL;

        globalThis.location.href = returnUrl;
      } else if (errors && errors.token) {
        reduxDispatch(setContent("tokenExpired"));
      } else if (errors && errors.password) {
        let errorMessage = errors["password"].join(". ");
        setShowError(true);
        setErrorMsg(errorMessage);
        return reduxDispatch(setCreatePasswordButtonLoader(false));
      } else {
        return reduxDispatch(setCreatePasswordButtonLoader(false));
      }
    });
  }

  function onPasswordsChanged(data) {
    if (isCreatePasswordButtonLoader) return;
    setShowError(false);
    const isError = submitted && data.password !== data.confirmedPassword;
    if (isError) {
      setShowError(true);
      setErrorMsg(VALIDATION_ERRORS["passwordMatch"]);
      reduxDispatch(setCreatePasswordButtonLoader(false));
    }
    setState({ ...state, ...data });
  }

  useEffect(() => {
    if (!isLoading) {
      return;
    }
    if (!isResetPassword) {
      setIsLoading(false);
      return;
    }
    reduxDispatch(checkEmail()).then(result => {
      const { t = false } = result.payload;
      reduxDispatch(setTwoFactorEnabled(t));
      setIsLoading(false);
    });
  }, [isResetPassword, isLoading]);

  if (isLoading) return null;

  return (
    <MainLayout>
      <div className="password-page-content">
        <h1 className="coheadline navyBlue">Create your password</h1>

        <div className="create-your-password-block">
          <InputField
            className="client-email"
            value={email}
            disabled
            label="Email address"
            name="email"
          />

          <PasswordField
            autoFocus
            className="client-password-set"
            disabled={isCreatePasswordButtonLoader}
            label="Password"
            name="password"
            onChange={e => onPasswordsChanged({ confirmedPassword, password: e.value })}
            onKeyPress={handlePasswordKeyPress}
            tabIndex="1"
            value={password}
            placeholder="Please create a memorable password"
          />

          <PasswordChecks password={password} />

          <PasswordField
            className="client-password-confirm"
            disabled={isCreatePasswordButtonLoader}
            errorMsg={errorMsg}
            label="Password"
            name="password"
            onChange={e => onPasswordsChanged({ confirmedPassword: e.value, password })}
            onKeyPress={handlePasswordKeyPress}
            placeholder="Confirm your password"
            showError={showError}
            tabIndex="2"
            value={confirmedPassword}
          />

          <Button
            customWidth="183px"
            disabled={isBtnDisabled}
            isLoading={isCreatePasswordButtonLoader}
            label="Create password"
            onClick={handleSubmitButton}
            tabIndex="3"
            variant="solid"
          />
        </div>
      </div>
    </MainLayout>
  );
}
